import { createFileRoute, Link } from "@tanstack/react-router";
import { requireCompleteProfile } from "@/lib/auth-guard";
import { useBookBySlug, useBookAction } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import {
  BooksPhaseOptions,
  Collections,
  type BooksResponse,
} from "@/types/pocketbase-types";
import BookQuizContainer from "@/components/application/books/BookQuizContainer";
import LoadingSpinner from "@/components/LoadingSpinner";
import Button from "@/components/Button";
import ErrorFallback from "@/components/ErrorFallback";
import { useEffect, useState, useRef } from "react";
import { pb } from "@/lib/pocketbase";

const BookDetailPage = () => {
  const { slug } = Route.useParams();

  // Fetch book data using unified query
  const { data: bookData, isLoading, error } = useBookBySlug(slug);

  // Action trigger mutation
  const actionMutation = useBookAction();

  // Query client for invalidation
  const queryClient = useQueryClient();

  // Real-time book state
  const [realtimeBook, setRealtimeBook] = useState<BooksResponse | null>(null);
  const autoActionTriggered = useRef<Set<string>>(new Set());

  // Get current book (prioritize real-time data over initial fetch)
  const currentBook = realtimeBook || bookData?.book;

  // Set up real-time subscription for book updates
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setupSubscription = async () => {
      if (!currentBook?.id) return;

      try {
        console.log("Setting up book subscription for:", currentBook.id);
        unsubscribe = await pb
          .collection(Collections.Books)
          .subscribe(currentBook.id, (data) => {
            const updatedBook = data.record as BooksResponse;
            console.log("Book update received:", {
              phase: updatedBook.phase,
              id: updatedBook.id,
            });
            setRealtimeBook(updatedBook);

            // Invalidate and refetch book data when processing is complete
            if (updatedBook.phase === BooksPhaseOptions.processed) {
              console.log(
                "Book processed, invalidating queries to fetch summary and questions"
              );
              queryClient.invalidateQueries({ queryKey: ["books", slug] });
            }
          });
      } catch (error) {
        console.error("Failed to set up book subscription:", error);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentBook?.id]);

  // Auto-trigger actions based on phase changes
  useEffect(() => {
    if (!currentBook?.id || actionMutation.isPending) return;

    const bookId = currentBook.id;
    const phase = currentBook.phase;
    const actionKey = `${bookId}-${phase}`;

    // Prevent duplicate auto-actions
    if (autoActionTriggered.current.has(actionKey)) return;

    const triggerAction = async (action: string) => {
      try {
        autoActionTriggered.current.add(actionKey);
        console.log(
          `Auto-triggering ${action} for book ${bookId} in phase ${phase}`
        );
        await actionMutation.mutateAsync({ bookId, action });
      } catch (error) {
        console.error(`Auto-${action} failed:`, error);
        // Remove from set so user can retry manually
        autoActionTriggered.current.delete(actionKey);
      }
    };

    // Auto-trigger download when book is searched
    if (phase === BooksPhaseOptions.searched) {
      triggerAction("download");
    }
    // Auto-trigger process when book is downloaded
    else if (phase === BooksPhaseOptions.downloaded) {
      triggerAction("process");
    }
  }, [currentBook?.id, currentBook?.phase, actionMutation]);

  if (isLoading && !bookData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] pt-24">
        <LoadingSpinner text="Loading book..." />
      </div>
    );
  }

  if (error || !currentBook) {
    return <ErrorFallback variant="fullpage" />;
  }

  const { summary, questions, isProcessing } = bookData || {};

  // Get book metadata for display (use real-time data)
  const bookTitle =
    currentBook?.title || currentBook?.user_title || "Processing Book";
  const bookAuthor = currentBook?.author || "";
  const bookId = currentBook?.id;
  const bookPhase = currentBook?.phase;

  // Handle action triggers
  const handleAction = async (action: string) => {
    if (!bookId) return;

    try {
      await actionMutation.mutateAsync({ bookId, action });
    } catch (error) {
      console.error("Action failed:", error);
    }
  };

  // Determine if we should show action buttons
  const showActionButtons =
    currentBook &&
    [
      BooksPhaseOptions.searched,
      BooksPhaseOptions.downloaded,
      BooksPhaseOptions.error,
    ].includes(currentBook.phase);

  return (
    <div className="p-7 flex flex-col lg:flex-row gap-y-4 lg:gap-x-7 pt-24">
      <div
        className="w-fill lg:w-[300px] h-fit bg-neutral-200 rounded-[15px] px-7 py-10 flex flex-col gap-y-12"
        id="addtimer"
      >
        <div className="flex flex-col">
          <h2 className="font-bold text-[24px]">{bookTitle}</h2>
          <p>{bookAuthor}</p>
          {currentBook && (
            <div className="mt-4">
              <p className="text-sm text-neutral-600">
                Phase:{" "}
                <span className="font-semibold">{currentBook.phase}</span>
              </p>
              {currentBook.retry_count > 0 && (
                <p className="text-sm text-neutral-500">
                  Retries: {currentBook.retry_count}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {showActionButtons && (
          <div className="flex flex-col gap-y-3">
            {currentBook.phase === BooksPhaseOptions.searched && (
              <div className="text-center">
                <p className="text-xs text-blue-500 mb-2">
                  Auto-downloading...
                </p>
                <Button
                  size="sm"
                  onClick={() => handleAction("download")}
                  disabled={actionMutation.isPending}
                  className="w-full"
                  variant="outline"
                >
                  Retry Download
                </Button>
              </div>
            )}

            {currentBook.phase === BooksPhaseOptions.downloaded && (
              <div className="text-center">
                <p className="text-xs text-blue-500 mb-2">Auto-processing...</p>
                <Button
                  size="sm"
                  onClick={() => handleAction("process")}
                  disabled={actionMutation.isPending}
                  className="w-full"
                  variant="outline"
                >
                  Retry Process
                </Button>
              </div>
            )}

            {currentBook.phase === BooksPhaseOptions.error && (
              <div className="text-center">
                <p className="text-sm text-error-500 font-medium mb-2">
                  Something went wrong
                </p>
                <p className="text-xs text-neutral-500 mb-4">
                  Please try again
                </p>
                <Link to="/books/search">
                  <Button size="sm" className="w-full">
                    Back to Book Search
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Timer - show only when quiz has actually started */}
        {/* Timer will be shown by BookQuizContainer when quiz starts */}
      </div>

      {/* Always render BookQuizContainer to handle subscription and all states */}
      <BookQuizContainer
        summaryId={summary?.id}
        bookId={bookId}
        bookSlug={slug}
        quizQuestions={questions}
        isProcessing={isProcessing || !bookId}
        bookPhase={bookPhase}
        quizTaken={(currentBook as any)?.quiz_taken ?? 0}
      />
    </div>
  );
};

export const Route = createFileRoute("/books/$slug")({
  ...requireCompleteProfile(),
  component: BookDetailPage,
});
